import styled from '@emotion/styled'
import { CONTEXT_ELEMENT_OP_AS_STR, ConditionGroupElement, StackContext } from '@pubstack/common/src/stackContext'
import { FunctionComponent, useEffect, useState } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import { Colors } from '~/assets/style/colors'
import { useScopePermissions } from '~/auth/useScopePermissions'
import Button from '~/components/Button'
import { Icon } from '~/components/Icon'
import { IllustrationMessage } from '~/components/IllustrationMessage'
import { Input } from '~/components/Input'
import { useGlobalFlyout } from '~/components/layout/GlobalFlyout'
import { useGlobalModal } from '~/components/layout/GlobalModal'
import { Link } from '~/components/Link'
import { Modal } from '~/components/Modal'
import { Pagination } from '~/components/Pagination'
import { Popover, PopoverContentWrapper, PopoverLine } from '~/components/Popover'
import { Tooltip } from '~/components/Tooltip'
import Table, { handleTableSort, onColumnSort, SortableColumn, TableColumns } from '~/components/table/Table'
import TableCell from '~/components/table/TableCell'
import TableRow from '~/components/table/TableRow'
import { useClientPagination } from '~/hooks/useClientPagination'
import { ContextConditions } from '~/modules/adm/ContextConditions'
import { SUPPORT_MAIL } from '~/utils/constants'
import { countStacksUse } from '../../rules/countStacksUsed'
import { PureUsedStackTooltip } from '../../rules/PureUsedStackTooltip'
import { PureAdmContextsDetailsFlyout } from './PureAdmContextsDetailsFlyout'
import { PureArchiveContextModal } from './PureArchiveContextModal'

type PureAdmContextsPageProps = {
  contexts: StackContext[]
  isLoading: boolean
  isError: boolean
  isModalVisible: boolean
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>
  scopeId?: string
  onCreateContext: () => void
  onArchiveContext: (context: StackContext) => Promise<void>
  onNavigateToArchived: () => void
  onCreateContextKey: () => void
  breadcrumbs: React.ReactNode
}

const ContextsWrapper = styled.div`
  padding: 0;
`

const ActionsTableCell = styled(TableCell)`
  width: 0; /** necessary to get the last cell to shrink down to hug action buttons */
`

const ButtonActions = styled.div`
  display: flex;
  gap: 16px;
  justify-content: flex-end;
`

const FilterActionForm = styled.form`
  margin: 12px 0;
  display: flex;
  width: 100%;
  justify-content: space-between;
`

export const PureAdmContextsNewContextModal: FunctionComponent<{ onCreateContextKey: () => void; onClose: () => void }> = ({ onCreateContextKey, onClose }) => {
  const { close } = useGlobalModal()
  return (
    <Modal.Content>
      <Modal.Title>New context</Modal.Title>

      <Modal.Body>
        A context is composed of logical conditions with one or more context keys. <br />
        As no context keys are set yet, you need to create some first.
      </Modal.Body>

      <Modal.Actions>
        <Button
          variant={'tertiary'}
          onClick={() => {
            close()
            onClose()
          }}
          trackingLabel={'adm-contexts-context-key-modal-cancel'}
        >
          Cancel
        </Button>
        <Button variant={'primary'} onClick={onCreateContextKey} trackingLabel={'adm-contexts-context-key-modal-new'}>
          New context key
        </Button>
      </Modal.Actions>
    </Modal.Content>
  )
}

const buildConditionAsString = (element: ConditionGroupElement, level = 0): string => {
  if ('elements' in element) {
    const part = `${element.elements.map((e) => buildConditionAsString(e, level + 1)).join(` ${element.op} `)}`
    return level === 0 ? part : `(${part})`
  }

  return `${element.key} ${CONTEXT_ELEMENT_OP_AS_STR[element.op]} [${element.values.join(', ')}]`
}

export const PureAdmContextsPage: FunctionComponent<PureAdmContextsPageProps> = ({
  contexts,
  isLoading,
  isError,
  isModalVisible,
  setIsOpen,
  onCreateContext,
  onArchiveContext,
  onNavigateToArchived,
  onCreateContextKey,
}) => {
  const flyout = useGlobalFlyout()
  const { canArchive, canCreate, disabledReason } = useScopePermissions()
  const [actionsOpen, setActionsOpen] = useState(false)
  const [columns, setColumns] = useState<TableColumns<StackContext>>([
    {
      name: 'Name',
      isSortable: true,
      order: 'ascending',
      attributeSort: 'name',
    },
    {
      name: 'Conditions',
      isSortable: false,
    },
    {
      name: 'Used on',
      isSortable: true,
      order: 'none',
      attributeSort: 'stacksUse',
    },
    {
      name: 'Actions',
      isSortable: false,
    },
  ])

  const { control } = useForm<{ search: string }>({
    defaultValues: {
      search: '',
    },
  })

  const search = useWatch({ control, name: 'search' })

  const sortAndGroup = (contexts: StackContext[]) => {
    const sortedColumn = (columns.find((c) => c.isSortable && c.order !== 'none') || columns[0]) as SortableColumn<StackContext>
    if (sortedColumn.attributeSort === 'stacksUse') {
      return [...contexts].sort((a, b) => (sortedColumn.order === 'ascending' ? 1 : -1) * (countStacksUse(b.stacksUse) - countStacksUse(a.stacksUse)))
    } else {
      return handleTableSort<StackContext>(columns, contexts).filter((context) => {
        const includeName = context.name.toLowerCase().includes(search.toLowerCase())
        const includeConditions = buildConditionAsString(context.conditions)?.toLowerCase().includes(search.toLowerCase())
        return includeName || includeConditions
      })
    }
  }
  // The archived ones live on their own page, so this list never shows them and never has to group them last.
  const sortedContexts = sortAndGroup(contexts)
  /* Paged in the browser: the endpoint answers with the whole list, and a scope with a few hundred
     contexts was one unbroken scroll. Narrowing the search clamps the page rather than stranding the
     reader on an empty one — see `useClientPagination`. */
  const { pageItems, paginationProps } = useClientPagination(sortedContexts)

  /* A context decides which stack a visitor is served, so making one is an owner's. The button stays on
     screen saying why — seeing that the capability exists is half of knowing to ask an owner for it. */
  const newContextButton = (
    <Tooltip title={disabledReason} disabled={canCreate}>
      <Button variant={'primary'} disabled={!canCreate} onClick={onCreateContext} trackingLabel={'adm-contexts-new'}>
        New context
      </Button>
    </Tooltip>
  )

  /**
   * The same pair the media and ad unit lists carry: the one act a reader comes here to do as a button,
   * everything else behind the kebab beside it. Archiving is the only other act there is, which is exactly
   * why it stopped being a button of its own — it was competing for the row with the one that matters.
   *
   * Owners only, because the archived page is owner-gated — a menu everyone can see leading to a 403 is
   * worse than no menu, so a `user` gets no kebab at all rather than an empty one.
   */
  const actionsMenu = canArchive && (
    <Popover
      align={'end'}
      open={actionsOpen}
      setOpen={setActionsOpen}
      trigger={<Button variant={'tertiary'} iconName={'more_vert'} title={'More actions'} trackingLabel={'adm-contexts-actions-view'} />}
    >
      <PopoverContentWrapper>
        <PopoverLine
          onClick={() => {
            setActionsOpen(false)
            onNavigateToArchived()
          }}
          data-tracking-label={'adm-contexts-archived-navigate'}
        >
          <Icon width={'20px'} name={'archive'} />
          <span>Archived contexts</span>
        </PopoverLine>
      </PopoverContentWrapper>
    </Popover>
  )

  const modal = useGlobalModal()
  // TODO cfo 2023-11-08 - Ugly special case. Need some rethinking
  useEffect(() => {
    if (isModalVisible && !modal.isOpen) {
      modal.open(PureAdmContextsNewContextModal, {
        onCreateContextKey: () => {
          onCreateContextKey()
          setIsOpen(false)
          modal.close()
        },
        onClose: () => {
          setIsOpen(false)
        },
      })
    }

    if (!modal.isOpen) {
      setIsOpen(false)
    }
  }, [isModalVisible])

  return (
    <ContextsWrapper>
      {isError ? (
        <IllustrationMessage
          iconName={'sync_problem'}
          iconSize={'190px'}
          title={'Something went wrong.'}
          message={
            <>
              We could not load your contexts. Please refresh the page or contact <Link label={SUPPORT_MAIL} href={`mailto:${SUPPORT_MAIL}`} trackingLabel={'common-mail-to-support-navigate'} /> if the
              issue persists.
            </>
          }
        />
      ) : isLoading || contexts?.length > 0 ? (
        <>
          <FilterActionForm>
            <Input name={'search'} type={'text'} iconLeft={'search'} labelIsPlaceholder label={'Search'} control={control} />
            <ButtonActions>
              {contexts?.length > 0 && newContextButton}
              {actionsMenu}
            </ButtonActions>
          </FilterActionForm>
          <Table columns={columns} isLoading={isLoading} onClickHeading={(column) => onColumnSort(columns, column, setColumns)} noWrapHeader={true}>
            {pageItems.map((context) => (
              <TableRow
                key={context.name}
                clickable={{
                  onClick: (event) => {
                    event.preventDefault()
                    event.stopPropagation()
                    flyout.open(PureAdmContextsDetailsFlyout, { color: Colors.Violet, width: `816px`, closeTrackingLabel: 'adm-contexts-detail-view-close' }, { context })
                  },
                  trackingLabel: 'adm-contexts-view',
                }}
              >
                {/* Bold spelled out rather than left to `Table`'s first-cell rule: that rule selects on
                    `TableCell`'s own class, and the width and wrapping here used to be a `styled(TableCell)`
                    wrapper whose separate class the selector never matched — so the name, the one thing a
                    reader scans this list by, came out lighter than the cells beside it. */}
                <TableCell className={'max-w-[300px] min-w-[200px] font-bold break-all'}>{context.name}</TableCell>
                <TableCell>
                  <ContextConditions group={context.conditions} inline={true} />
                </TableCell>
                <TableCell>{context.stacksUse ? <PureUsedStackTooltip stacksBySite={context.stacksUse} /> : <span>0 stack</span>} </TableCell>

                <ActionsTableCell>
                  <ButtonActions>
                    <Tooltip title={'View'}>
                      <Button
                        iconName={'view'}
                        variant={'tertiary'}
                        title={'View'}
                        onClick={(event) => {
                          event.preventDefault()
                          event.stopPropagation()
                          flyout.open(PureAdmContextsDetailsFlyout, { color: Colors.Violet, width: `816px`, closeTrackingLabel: 'adm-contexts-detail-view-close' }, { context })
                        }}
                        trackingLabel={'adm-contexts-detail-icon-view'}
                      />
                    </Tooltip>
                    {/* Archiving is the owner's call and the API says so too, so a user below owner is not
                        offered a button that would come back a 403. Unarchiving lives on the archived page. */}
                    {canArchive && (
                      <Tooltip title={context.stacksUse ? 'A used context cannot be archived.' : 'Archive'} positions={['left']}>
                        <Button
                          iconName={'archive'}
                          variant={'tertiary'}
                          title={'Archive'}
                          onClick={(event) => {
                            event.preventDefault()
                            event.stopPropagation()
                            modal.open(PureArchiveContextModal, {
                              contextName: context.name,
                              onArchive: async () => {
                                await onArchiveContext(context)
                                modal.close()
                              },
                            })
                          }}
                          disabled={!!context.stacksUse}
                          trackingLabel={'adm-contexts-archive'}
                        />
                      </Tooltip>
                    )}
                  </ButtonActions>
                </ActionsTableCell>
              </TableRow>
            ))}
          </Table>
          <Pagination {...paginationProps} className={'mt-4'} />
        </>
      ) : (
        <IllustrationMessage
          iconName={'night_sky'}
          iconSize={'190px'}
          title={'No context.'}
          message={
            <>
              Contexts give you flexibility and allow you to choose different scenarios for your stack, <br />
              according to the settings you defined with context keys.
            </>
          }
        >
          <ButtonActions>
            {/* A scope whose contexts are all archived lands here too, and the kebab is the only way back. */}
            {newContextButton}
            {actionsMenu}
          </ButtonActions>
        </IllustrationMessage>
      )}
    </ContextsWrapper>
  )
}
